import type { OrganizationProfile } from '@pulsify/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { BadgeCheck, Building2, Check, ChevronsUpDown, Plus, Store } from 'lucide-react'
import { useEffect } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { setActiveProfileId, useOrganizationProfiles } from '~/entities/profile/model/use-profiles'
import * as m from '~/paraglide/messages'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/shared/ui/dropdown-menu'

function ProfileFace({ profile }: { profile: OrganizationProfile }) {
  return (
    <Avatar className="squircle size-6 rounded-md">
      {profile.avatarUrl ? (
        <img src={profile.avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <AvatarFallback className="squircle rounded-md font-semibold text-[11px]">
          {profile.name.slice(0, 1).toUpperCase()}
        </AvatarFallback>
      )}
    </Avatar>
  )
}

function kindLabel(profile: OrganizationProfile): string {
  return profile.kind === 'place' ? m.profileKindPlace() : m.profileKindOrganizer()
}

/** Picks the profile the dashboard works on; shown only when profiles are switched on. */
export function ProfileSwitcher() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()
  const { enabled, profiles, active, storedId } = useOrganizationProfiles(business?.id)

  useEffect(() => {
    if (enabled && business && active && storedId !== active.id) {
      setActiveProfileId(business.id, active.id)
    }
  }, [enabled, business, active, storedId])

  if (!enabled || !business || !active) return null

  const organization = business.companyName || business.legalName
  const groups = [
    {
      key: 'organizers',
      label: m.profileGroupOrganizers(),
      items: profiles.filter((profile) => profile.kind !== 'place'),
    },
    {
      key: 'places',
      label: m.profileGroupPlaces(),
      items: profiles.filter((profile) => profile.kind === 'place'),
    },
  ].filter((group) => group.items.length > 0)

  const pick = (profile: OrganizationProfile) => {
    if (profile.id === active.id) return
    setActiveProfileId(business.id, profile.id)
    navigate({ to: '/dashboard' })
    queryClient.invalidateQueries().catch(() => undefined)
  }

  return (
    <div className="px-3 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={m.profileSwitcherLabel()}
            className="squircle flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
          >
            <ProfileFace profile={active} />
            <span className="min-w-0 flex-1 truncate font-medium text-sm">{active.name}</span>
            <span className="shrink-0 text-muted-foreground text-xs">{kindLabel(active)}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{organization}</span>
              <span className="block text-muted-foreground text-xs">{m.profileOrganization()}</span>
            </span>
          </DropdownMenuLabel>
          {groups.map((group) => (
            <div key={group.key}>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="py-1 text-muted-foreground text-xs">
                {group.label}
              </DropdownMenuLabel>
              {group.items.map((profile) => (
                <DropdownMenuItem
                  key={profile.id}
                  onSelect={() => pick(profile)}
                  className="gap-2 pl-4"
                >
                  {profile.kind === 'place' ? (
                    <Store className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <BadgeCheck className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="flex-1 truncate">{profile.name}</span>
                  {profile.isPublished ? null : (
                    <span className="text-muted-foreground text-xs">{m.profileDraft()}</span>
                  )}
                  {profile.id === active.id ? <Check className="h-4 w-4" /> : null}
                </DropdownMenuItem>
              ))}
            </div>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => navigate({ to: '/setup/profile', search: { kind: 'organizer' } })}
            className="gap-2"
          >
            <Plus className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 truncate">{m.profileNewOrganizer()}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => navigate({ to: '/setup/profile', search: { kind: 'place' } })}
            className="gap-2"
          >
            <Plus className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 truncate">{m.profileNewPlace()}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
